// Calendar (timeline) layout for the itinerary: hour grid spanning the full day,
// lane-packed events (computeEventLanes), drag/long-press to reschedule on
// touch, and 1D/3D/All day windows. Times are decimal hours; pixels are
// (h - CAL_START_HOUR) * CAL_HOUR_PX.
import React from 'react';
import { PALETTE } from '../../theme';
import { Icon } from '../../components/UI';
import { bookingInfoForIdea } from '../../utils/bookingLinks';
import { parseFlightHM, computeEventLanes } from './logic';

export const CAL_CAT_COLORS = {
  Food:        { bg: '#FDF0E8', border: '#C06040', text: '#7A3520' },
  Sights:      { bg: '#F4EEE4', border: '#8A7050', text: '#504028' },
  Experience:  { bg: '#EFE8F6', border: '#6050A0', text: '#342450' },
  'Day trip':  { bg: '#E6F4EC', border: '#507868', text: '#2A4840' },
  Nightlife:   { bg: '#E0E4F0', border: '#3A4870', text: '#1A2035' },
  Shopping:    { bg: '#F5EAF4', border: '#907090', text: '#584060' },
  Hotel:       { bg: '#E4EEF6', border: '#5A7898', text: '#304858' },
  Custom:      { bg: '#F6EAEA', border: '#907070', text: '#584040' },
};
// Unplaced-list coloring for multi-city trips — colored by leg order (leg 0
// gets palette[0], leg 1 gets palette[1], ...) so the parking lot visually
// groups by city instead of activity category.
const LEG_COLOR_PALETTE = [
  { bg: '#FDF0E8', border: '#C06040', text: '#7A3520' },
  { bg: '#E4EEF6', border: '#5A7898', text: '#304858' },
  { bg: '#EFE8F6', border: '#6050A0', text: '#342450' },
  { bg: '#E6F4EC', border: '#507868', text: '#2A4840' },
  { bg: '#F5EAF4', border: '#907090', text: '#584060' },
  { bg: '#FFF8EB', border: '#D97706', text: '#7A4E06' },
];
// Ideas tagged with a legIndex (multi-city trips — see data.js#getIdeasForLegs)
// get colored/grouped by leg instead of category; untagged ideas keep the
// category coloring so single-destination trips are unaffected.
export function unplacedColorFor(idea) {
  if (idea?.legIndex != null) return LEG_COLOR_PALETTE[idea.legIndex % LEG_COLOR_PALETTE.length];
  return CAL_CAT_COLORS[idea?.cat] || CAL_CAT_COLORS.Custom;
}
export function sortIdsByLeg(ids, allIdeas) {
  return [...ids].sort((a, b) => {
    const ia = allIdeas.find(i => i.id === a)?.legIndex ?? Infinity;
    const ib = allIdeas.find(i => i.id === b)?.legIndex ?? Infinity;
    return ia - ib;
  });
}
// Colored by position in the trip's chronological flight order — see
// FLIGHT_BAND_PALETTE in ItineraryScreen.js.
const FLIGHT_EVENT_PALETTE = [
  { bg: '#EFF6FF', border: '#93C5FD', label: '#3B82F6', mono: '#1E40AF' },
  { bg: '#F5F0FF', border: '#C4B0F5', label: '#8B5CF6', mono: '#6D28D9' },
  { bg: '#FFF8EB', border: '#FBCF7E', label: '#D97706', mono: '#B45309' },
  { bg: '#ECFDF5', border: '#6EE7B7', label: '#10B981', mono: '#047857' },
];
// Booked activities (bookings.activities_details.legs) — a single distinct
// teal so they read as "booked", not confusable with any CAL_CAT_COLORS
// idea category already on the grid.
const ACTIVITY_EVENT_COLOR = { bg: '#E6F7F5', border: '#2A9D8F', label: '#2A9D8F', mono: '#1B6E63' };
// Base pixels-per-hour at the default 60-min block size. Each rendered block
// row is always BASE_HOUR_PX tall regardless of interval — a 2-hour block
// setting halves px-per-hour, which is what actually "compresses" the grid
// to fit more of the day on screen, per user request.
const BASE_HOUR_PX = 64;
export const CAL_SETTINGS_KEY = 'tp_calendarViewSettings';
const DEFAULT_CAL_SETTINGS = { startHour: 0, endHour: 24, blockMinutes: 60 };
const BLOCK_OPTIONS = [
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
  { value: 240, label: '4 hours' },
];
export function loadCalSettings() {
  try {
    const raw = localStorage.getItem(CAL_SETTINGS_KEY);
    if (!raw) return DEFAULT_CAL_SETTINGS;
    const parsed = JSON.parse(raw);
    const startHour = Number.isFinite(parsed.startHour) ? parsed.startHour : DEFAULT_CAL_SETTINGS.startHour;
    const endHour = Number.isFinite(parsed.endHour) ? parsed.endHour : DEFAULT_CAL_SETTINGS.endHour;
    const blockMinutes = Number.isFinite(parsed.blockMinutes) ? parsed.blockMinutes : DEFAULT_CAL_SETTINGS.blockMinutes;
    return endHour > startHour ? { startHour, endHour, blockMinutes } : DEFAULT_CAL_SETTINGS;
  } catch { return DEFAULT_CAL_SETTINGS; }
}
function hourLabel(h) {
  if (h >= 24) return '12:00 AM (midnight)';
  const ap = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:00 ${ap}`;
}
export function CalendarSettingsPopover({ settings, onChange, onClose }) {
  const fieldStyle = { fontFamily: 'Inter', fontSize: 12, padding: '5px 6px', borderRadius: 6, border: `1px solid ${PALETTE.rule}`, background: PALETTE.paper, color: PALETTE.ink };
  return (
    <div data-cal-settings="1" style={{
      position: 'absolute', top: '100%', right: 12, marginTop: 4, zIndex: 20,
      background: PALETTE.paper, border: `0.5px solid ${PALETTE.rule}`, borderRadius: 10,
      boxShadow: '0 8px 24px rgba(0,0,0,0.14)', padding: 14, width: 210,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 700, color: PALETTE.ink }}>View settings</span>
        <button onClick={onClose} aria-label="Close view settings" style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, display: 'flex', padding: 2 }}>
          <Icon name="x" size={12} />
        </button>
      </div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontFamily: 'Inter', fontSize: 10, color: PALETTE.inkMute }}>Day starts</span>
        <select value={settings.startHour} onChange={(e) => onChange({ startHour: Number(e.target.value) })} style={fieldStyle}>
          {Array.from({ length: 24 }, (_, h) => h).map(h => <option key={h} value={h}>{hourLabel(h)}</option>)}
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontFamily: 'Inter', fontSize: 10, color: PALETTE.inkMute }}>Day ends</span>
        <select value={settings.endHour} onChange={(e) => onChange({ endHour: Number(e.target.value) })} style={fieldStyle}>
          {Array.from({ length: 24 }, (_, i) => i + 1).map(h => <option key={h} value={h}>{hourLabel(h)}</option>)}
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontFamily: 'Inter', fontSize: 10, color: PALETTE.inkMute }}>Block size</span>
        <select value={settings.blockMinutes} onChange={(e) => onChange({ blockMinutes: Number(e.target.value) })} style={fieldStyle}>
          {BLOCK_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    </div>
  );
}
const COARSE_POINTER = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)')?.matches;
// 100vh over-measures on mobile browsers while the URL bar is visible; prefer dvh
const VH_FULL = typeof CSS !== 'undefined' && CSS.supports?.('height', '100dvh') ? '100dvh' : '100vh';
const LONG_PRESS_MS = 350;
const PRESS_SLOP_PX = 10;
export function CalendarView({
  displayDays, accent, localOverrides,
  onEditTime, onAddAtTime, onEditItem, onMoveToDayAtTime,
  onMoveToParking, onRemoveFromTrip, onPlaceFromParking,
  onEditDuration, onResizeTop, onToggleConfirmed,
  displayOverflow, allIdeas, flightByDay = {}, activityByDay = {},
  accommodations = {}, accomBookedByDay = {}, bookings = {},
  calMode, calDayOffset, setCalDayOffset,
  destination = '', onOpenLeg,
  calSettings = DEFAULT_CAL_SETTINGS,
  dayNotes = {}, onSaveAccommodation, onSaveDayNote,
}) {
  const parsedCalN = parseInt(calMode, 10);
  const colsPerView = calMode === 'day' ? 1 : calMode === '3day' ? 3 : calMode === 'all' ? displayDays.length : (!isNaN(parsedCalN) && parsedCalN > 0 ? Math.min(parsedCalN, displayDays.length) : displayDays.length);
  const maxOffset = Math.max(0, displayDays.length - colsPerView);
  const safeOffset = Math.min(calDayOffset, maxOffset);
  const visibleDays = displayDays.slice(safeOffset, safeOffset + colsPerView);

  const scrollRef = React.useRef(null);
  const colRefs = React.useRef([]);
  const parkingRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const [ghost, setGhost] = React.useState(null);
  const [ctxMenu, setCtxMenu] = React.useState(null); // { idea, x, y, dayIdx }
  const [parkingHighlight, setParkingHighlight] = React.useState(false);
  const [parkingOpen, setParkingOpen] = React.useState(false);
  const [editingAccomDay, setEditingAccomDay] = React.useState(null);
  const [accomDraft, setAccomDraft] = React.useState('');
  const [noteDay, setNoteDay] = React.useState(null);
  const [noteDraft, setNoteDraft] = React.useState('');

  const CAL_START_HOUR = calSettings.startHour;
  const CAL_END_HOUR = calSettings.endHour;
  const blockHours = calSettings.blockMinutes / 60;
  const CAL_HOUR_PX = BASE_HOUR_PX / blockHours;
  const CAL_HOURS = React.useMemo(() => {
    const arr = [];
    for (let h = CAL_START_HOUR; h < CAL_END_HOUR - 1e-6; h += blockHours) arr.push(h);
    return arr;
  }, [CAL_START_HOUR, CAL_END_HOUR, blockHours]);

  const snapTo = (h) => Math.round(h * 4) / 4;
  const clampH = (h) => Math.max(CAL_START_HOUR, Math.min(CAL_END_HOUR - 0.25, h));
  const effectiveStart = (slot) => localOverrides[slot.idea.id]?.startTime ?? slot.start;
  const effectiveDur = (slot) => localOverrides[slot.idea.id]?.dur ?? slot.idea.dur;

  // getBoundingClientRect().top already accounts for scroll — do NOT add scrollTop
  const yToHour = (clientY, colEl) => {
    const rect = colEl.getBoundingClientRect();
    return clampH(snapTo((clientY - rect.top) / CAL_HOUR_PX + CAL_START_HOUR));
  };
  // Same conversion, but clamped to the true end of the day instead of
  // reserving the last 15 minutes. yToHour's -0.25 reservation exists so a
  // *start* position always leaves room for a minimum-length activity — but
  // applying that same reservation to an *end* position (dragging to create
  // an event, or dragging an event's bottom edge to extend it) meant you
  // could never drag all the way to midnight; the latest reachable time was
  // always 11:45pm. Used only where the live point represents where an
  // activity ends, not where one starts.
  const yToHourEnd = (clientY, colEl) => {
    const rect = colEl.getBoundingClientRect();
    return Math.max(CAL_START_HOUR, Math.min(CAL_END_HOUR, snapTo((clientY - rect.top) / CAL_HOUR_PX + CAL_START_HOUR)));
  };

  const xToVisIdx = (clientX) => {
    for (let i = 0; i < colRefs.current.length; i++) {
      const col = colRefs.current[i];
      if (!col) continue;
      const r = col.getBoundingClientRect();
      if (clientX >= r.left && clientX < r.right) return i;
    }
    return -1;
  };

  const isOverParkingLot = (clientX, clientY) => {
    if (!parkingRef.current) return false;
    const r = parkingRef.current.getBoundingClientRect();
    return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  };

  const handleWindowMove = React.useRef(null);
  const handleWindowUp = React.useRef(null);
  const touchBlockerRef = React.useRef(null);

  // Long-press to drag on touch. Until the hold timer fires the gesture stays
  // native (pan-y scroll); a quick tap runs onTap instead of dragging.
  const pressRef = React.useRef(null);
  const cancelPress = React.useCallback(() => {
    const p = pressRef.current;
    if (!p) return;
    pressRef.current = null;
    clearTimeout(p.timer);
    window.removeEventListener('pointermove', p.onMove);
    window.removeEventListener('pointerup', p.onUp);
    window.removeEventListener('pointercancel', p.onCancel);
  }, []);
  const schedulePress = (e, activate, onTap) => {
    cancelPress();
    const startX = e.clientX, startY = e.clientY;
    const p = {
      onMove: (ev) => { if (Math.hypot(ev.clientX - startX, ev.clientY - startY) > PRESS_SLOP_PX) cancelPress(); },
      onUp: () => { cancelPress(); onTap?.(); },
      onCancel: () => cancelPress(),
      timer: setTimeout(() => {
        if (pressRef.current !== p) return;
        cancelPress();
        try { navigator.vibrate?.(15); } catch {}
        activate();
      }, LONG_PRESS_MS),
    };
    pressRef.current = p;
    window.addEventListener('pointermove', p.onMove);
    window.addEventListener('pointerup', p.onUp);
    window.addEventListener('pointercancel', p.onCancel);
  };

  const startDrag = (state) => {
    dragRef.current = state;
    if (state.touch) {
      // Once a touch drag is active, stop the browser from scrolling mid-drag
      touchBlockerRef.current = (ev) => ev.preventDefault();
      document.addEventListener('touchmove', touchBlockerRef.current, { passive: false });
    }

    handleWindowMove.current = (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      drag.moved = true;

      if (drag.type === 'create') {
        const hour = yToHourEnd(e.clientY, drag.col);
        const lo = Math.min(drag.startHour, hour);
        const hi = Math.max(drag.startHour, hour);
        drag.ghostStart = lo; drag.ghostDur = hi - lo;
        setGhost({ type: 'create', dayIdx: drag.actualDayIdx, startHour: lo, dur: hi - lo });

      } else if (drag.type === 'move') {
        const overParking = isOverParkingLot(e.clientX, e.clientY);
        drag.overParking = overParking;
        setParkingHighlight(overParking);
        if (!overParking) {
          const vi = xToVisIdx(e.clientX);
          const targetVisIdx = vi >= 0 ? vi : drag.origVisIdx;
          const targetCol = colRefs.current[targetVisIdx] || drag.col;
          drag.currentVisIdx = targetVisIdx;
          drag.currentDayIdx = safeOffset + targetVisIdx;
          const newStart = clampH(snapTo(yToHour(e.clientY, targetCol) - drag.grabOffset));
          drag.ghostStart = newStart;
          setGhost({ type: 'move', dayIdx: drag.currentDayIdx, sourceDayIdx: drag.actualDayIdx, startHour: newStart, dur: drag.dur, ideaId: drag.ideaId });
        } else {
          setGhost(null);
        }

      } else if (drag.type === 'resize-top') {
        const hour = yToHour(e.clientY, drag.col);
        const newStart = clampH(snapTo(hour));
        const newDur = Math.max(0.25, drag.origEnd - newStart);
        drag.ghostStart = newStart; drag.ghostDur = newDur;
        setGhost({ type: 'resize', dayIdx: drag.actualDayIdx, startHour: newStart, dur: newDur, ideaId: drag.ideaId });

      } else if (drag.type === 'resize-bottom') {
        const hour = yToHourEnd(e.clientY, drag.col);
        const newDur = Math.max(0.25, hour - drag.origStart);
        drag.ghostDur = newDur;
        setGhost({ type: 'resize', dayIdx: drag.actualDayIdx, startHour: drag.origStart, dur: newDur, ideaId: drag.ideaId });

      } else if (drag.type === 'from-parking') {
        const vi = xToVisIdx(e.clientX);
        if (vi >= 0) {
          const col = colRefs.current[vi];
          const hour = yToHour(e.clientY, col);
          drag.targetDayIdx = safeOffset + vi;
          drag.ghostStart = hour;
          setGhost({ type: 'create', dayIdx: safeOffset + vi, startHour: hour, dur: drag.idea?.dur || 1 });
        } else {
          drag.targetDayIdx = -1;
          setGhost(null);
        }
      }
    };

    handleWindowUp.current = () => {
      const drag = dragRef.current;
      dragRef.current = null;
      window.removeEventListener('pointermove', handleWindowMove.current);
      window.removeEventListener('pointerup', handleWindowUp.current);
      window.removeEventListener('pointercancel', handleWindowUp.current);
      if (touchBlockerRef.current) {
        document.removeEventListener('touchmove', touchBlockerRef.current);
        touchBlockerRef.current = null;
      }
      setGhost(null);
      setParkingHighlight(false);
      if (!drag) return;

      if (drag.type === 'create') {
        const startH = drag.ghostStart ?? drag.startHour;
        const dur = drag.ghostDur ?? 0;
        onAddAtTime(drag.actualDayIdx, startH, dur < 0.5 ? null : dur);

      } else if (drag.type === 'move' && drag.moved) {
        if (drag.overParking) {
          onMoveToParking(drag.ideaId);
        } else {
          const newStart = drag.ghostStart ?? drag.origStart;
          const newDayIdx = drag.currentDayIdx ?? drag.actualDayIdx;
          if (newDayIdx !== drag.actualDayIdx) {
            onMoveToDayAtTime(drag.ideaId, newDayIdx, newStart);
          } else if (Math.abs(newStart - drag.origStart) >= 0.25) {
            onEditTime(drag.ideaId, newStart);
          }
        }

      } else if (drag.type === 'resize-top' && drag.moved) {
        if (drag.ghostStart != null) onResizeTop(drag.ideaId, drag.ghostStart, drag.ghostDur ?? drag.origDur);

      } else if (drag.type === 'resize-bottom' && drag.moved) {
        if (drag.ghostDur != null) onEditDuration(drag.ideaId, Math.max(0.25, drag.ghostDur));

      } else if (drag.type === 'from-parking') {
        if (drag.targetDayIdx >= 0 && drag.ghostStart != null) {
          onPlaceFromParking(drag.ideaId, drag.targetDayIdx, drag.ghostStart);
        }
      }
    };

    window.addEventListener('pointermove', handleWindowMove.current);
    window.addEventListener('pointerup', handleWindowUp.current);
    window.addEventListener('pointercancel', handleWindowUp.current);
  };

  React.useEffect(() => {
    if (scrollRef.current) {
      const targetHour = Math.max(CAL_START_HOUR, Math.min(CAL_END_HOUR, 8));
      scrollRef.current.scrollTop = (targetHour - CAL_START_HOUR) * CAL_HOUR_PX - 10;
    }
    return () => {
      cancelPress();
      if (handleWindowMove.current) window.removeEventListener('pointermove', handleWindowMove.current);
      if (handleWindowUp.current) {
        window.removeEventListener('pointerup', handleWindowUp.current);
        window.removeEventListener('pointercancel', handleWindowUp.current);
      }
      if (touchBlockerRef.current) document.removeEventListener('touchmove', touchBlockerRef.current);
    };
  }, []); // eslint-disable-line

  // Dismiss context menu on outside click
  React.useEffect(() => {
    if (!ctxMenu) return;
    const dismiss = (e) => {
      if (!e.target.closest('[data-ctxmenu]')) setCtxMenu(null);
    };
    window.addEventListener('pointerdown', dismiss);
    return () => window.removeEventListener('pointerdown', dismiss);
  }, [ctxMenu]);

  // Dismiss the unplaced-items panel on outside click
  React.useEffect(() => {
    if (!parkingOpen) return;
    const dismiss = (e) => {
      if (!e.target.closest('[data-parking-panel]')) setParkingOpen(false);
    };
    window.addEventListener('pointerdown', dismiss);
    return () => window.removeEventListener('pointerdown', dismiss);
  }, [parkingOpen]);

  // Dismiss the day-note popover on outside click
  React.useEffect(() => {
    if (noteDay === null) return;
    const dismiss = (e) => {
      if (!e.target.closest('[data-day-note]')) setNoteDay(null);
    };
    window.addEventListener('pointerdown', dismiss);
    return () => window.removeEventListener('pointerdown', dismiss);
  }, [noteDay]);

  const isTouchLike = (e) => e.pointerType !== 'mouse';

  const handleColPointerDown = (e, visIdx) => {
    if (!isTouchLike(e) && e.button !== 0) return;
    const col = colRefs.current[visIdx];
    if (!col) return;
    const hour = yToHour(e.clientY, col);
    const begin = (touch) => {
      startDrag({ type: 'create', actualDayIdx: safeOffset + visIdx, col, startHour: hour, moved: false, touch });
      setGhost({ type: 'create', dayIdx: safeOffset + visIdx, startHour: hour, dur: 0 });
    };
    if (isTouchLike(e)) { schedulePress(e, () => begin(true)); return; }
    begin(false);
    e.preventDefault();
  };

  const handleEventPointerDown = (e, slot, visIdx) => {
    if (!isTouchLike(e) && e.button !== 0) return;
    e.stopPropagation();
    const col = colRefs.current[visIdx];
    if (!col) return;
    const origStart = effectiveStart(slot);
    const dur = effectiveDur(slot);
    const grabOffset = yToHour(e.clientY, col) - origStart;
    const begin = (touch) => {
      startDrag({
        type: 'move', actualDayIdx: safeOffset + visIdx, origVisIdx: visIdx,
        currentVisIdx: visIdx, currentDayIdx: safeOffset + visIdx,
        col, ideaId: slot.idea.id, origStart, grabOffset, dur, moved: false, touch,
      });
      setGhost({ type: 'move', dayIdx: safeOffset + visIdx, sourceDayIdx: safeOffset + visIdx, startHour: origStart, dur, ideaId: slot.idea.id });
    };
    if (isTouchLike(e)) { schedulePress(e, () => begin(true), () => onEditItem(slot.idea)); return; }
    begin(false);
    e.preventDefault();
  };

  const handleResizePointerDown = (e, slot, visIdx, edge) => {
    if (!isTouchLike(e) && e.button !== 0) return;
    e.stopPropagation();
    const col = colRefs.current[visIdx];
    if (!col) return;
    const origStart = effectiveStart(slot);
    const origDur = effectiveDur(slot);
    const begin = (touch) => {
      startDrag({
        type: edge === 'top' ? 'resize-top' : 'resize-bottom',
        actualDayIdx: safeOffset + visIdx, col,
        ideaId: slot.idea.id, origStart, origDur, origEnd: origStart + origDur, moved: false, touch,
      });
      setGhost({ type: 'resize', dayIdx: safeOffset + visIdx, startHour: origStart, dur: origDur, ideaId: slot.idea.id });
    };
    if (isTouchLike(e)) { schedulePress(e, () => begin(true)); return; }
    begin(false);
    e.preventDefault();
  };

  const handleParkingPointerDown = (e, idea) => {
    if (!isTouchLike(e) && e.button !== 0) return;
    e.stopPropagation();
    const begin = (touch) => startDrag({ type: 'from-parking', ideaId: idea.id, idea, targetDayIdx: -1, moved: false, touch });
    if (isTouchLike(e)) { schedulePress(e, () => begin(true)); return; }
    begin(false);
    e.preventDefault();
  };

  // Swipe horizontally on the grid to page between days (touch only)
  const swipeRef = React.useRef(null);
  const handleSwipePointerDown = (e) => {
    if (!isTouchLike(e)) return;
    swipeRef.current = { x: e.clientX, y: e.clientY };
  };
  const handleSwipePointerUp = (e) => {
    const s = swipeRef.current;
    swipeRef.current = null;
    if (!s || dragRef.current || colsPerView >= displayDays.length) return;
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      setCalDayOffset(dx < 0 ? Math.min(maxOffset, safeOffset + 1) : Math.max(0, safeOffset - 1));
    }
  };

  const fmtH = (h) => {
    const rawHr = Math.floor(h); const min = Math.round((h - rawHr) * 60);
    // Same hour-24-is-midnight normalization as logic.js#toTimeStr — an
    // event can now legitimately end at hour 24 (see yToHourEnd above).
    const hr = rawHr % 24;
    const ap = hr >= 12 ? 'pm' : 'am'; const h12 = hr > 12 ? hr - 12 : hr === 0 ? 12 : hr;
    return `${h12}${min ? ':' + String(min).padStart(2, '0') : ''}${ap}`;
  };

  const MIN_COL_W = 130;
  const HANDLE_H = COARSE_POINTER ? 14 : 7;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: `calc(${VH_FULL} - 145px)`, overflow: 'hidden', userSelect: 'none', position: 'relative' }}>
      {/* Nav bar: day paging — only shown when there are more days than fit
          on screen. View settings now live in the toolbar above, next to
          Map, instead of taking a whole row here. */}
      {colsPerView < displayDays.length && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', borderBottom: `0.5px solid ${PALETTE.ruleSoft}`, flexShrink: 0, background: PALETTE.paper }}>
          <button onClick={() => setCalDayOffset(Math.max(0, safeOffset - 1))} disabled={safeOffset === 0} aria-label="Earlier days"
            style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: 'transparent', borderRadius: 5, cursor: safeOffset === 0 ? 'default' : 'pointer', color: safeOffset === 0 ? PALETTE.rule : PALETTE.ink, padding: '3px 7px', display: 'flex', alignItems: 'center' }}>
            <Icon name="arrowL" size={12} />
          </button>
          <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 0.5, flex: 1, textAlign: 'center' }}>
            {visibleDays.map(d => `${d.label} ${d.date}${d.destCity ? ` · ${d.destCity}` : ''}`).join('  ·  ')}
          </span>
          <button onClick={() => setCalDayOffset(Math.min(maxOffset, safeOffset + 1))} disabled={safeOffset >= maxOffset} aria-label="Later days"
            style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: 'transparent', borderRadius: 5, cursor: safeOffset >= maxOffset ? 'default' : 'pointer', color: safeOffset >= maxOffset ? PALETTE.rule : PALETTE.ink, padding: '3px 7px', display: 'flex', alignItems: 'center' }}>
            <Icon name="arrow" size={12} />
          </button>
        </div>
      )}

      {/* Unplaced items — a floating toggle instead of a permanent top strip,
          so it doesn't eat calendar height or dominate the view. The wrapper
          (button + panel) stays the drop-zone ref regardless of open/closed,
          so dragging an event up here still unschedules it without opening
          the panel first. Opening it reveals a non-modal list you can drag
          straight onto the grid behind/beside it. */}
      <div
        ref={parkingRef}
        data-parking-panel="1"
        style={{ position: 'absolute', top: colsPerView < displayDays.length ? 40 : 8, right: 10, zIndex: 15 }}
      >
        <button
          onClick={() => setParkingOpen(o => !o)}
          aria-expanded={parkingOpen}
          style={{
            appearance: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 10px', borderRadius: 999,
            background: parkingHighlight ? `${accent}18` : PALETTE.paper,
            border: `1.5px ${parkingHighlight ? 'dashed' : 'solid'} ${parkingHighlight ? accent : PALETTE.rule}`,
            color: parkingHighlight ? accent : PALETTE.inkMute,
            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 0.6, textTransform: 'uppercase',
            transition: 'background 0.15s',
          }}
        >
          {parkingHighlight ? 'Drop to unschedule' : `Unplaced${displayOverflow.length > 0 ? ` · ${displayOverflow.length}` : ''}`}
          <Icon name={parkingOpen ? 'arrowUp' : 'arrowDn'} size={10} />
        </button>
        {parkingOpen && (
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 16,
            width: 240, maxHeight: 320, overflowY: 'auto', scrollbarWidth: 'thin',
            background: PALETTE.paper, border: `0.5px solid ${PALETTE.rule}`, borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.16)', padding: 8,
            display: 'flex', flexDirection: 'column', gap: 5,
          }}>
            {displayOverflow.length === 0 ? (
              <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, fontStyle: 'italic', padding: '8px 6px' }}>
                Nothing unplaced — drag an event onto this button to unschedule it.
              </span>
            ) : sortIdsByLeg(displayOverflow, allIdeas).map(id => {
              const idea = allIdeas.find(i => i.id === id);
              if (!idea) return null;
              const cc = unplacedColorFor(idea);
              return (
                <div
                  key={id}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 10px', borderRadius: 8, background: cc.bg, border: `1px solid ${cc.border}`, fontFamily: 'Inter', fontSize: 12, fontWeight: 500, color: cc.text, cursor: 'grab' }}
                  onPointerDown={(e) => handleParkingPointerDown(e, idea)}
                >
                  {idea.destCity && (
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 0.3, textTransform: 'uppercase', flexShrink: 0, opacity: 0.75 }}>{idea.destCity}</span>
                  )}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{idea.title}</span>
                </div>
              );
            })}
            {displayOverflow.length > 0 && (
              <span style={{ fontFamily: 'Inter', fontSize: 10, color: PALETTE.inkMute, padding: '3px 6px 0' }}>
                Drag onto a day to place it
              </span>
            )}
          </div>
        )}
      </div>

      {/* ONE scroll container for header + grid + footer, both axes. CSS
          `position: sticky` only respects its NEAREST scrolling ancestor, so
          nesting a second (vertical-only) scroll box in here would silently
          break "left: 0" stickiness on the time gutter the moment this outer
          box scrolls horizontally — sticky header (top) and sticky time
          gutter (left) both need to live in the same scrolling box as the
          horizontal scrollbar that moves them. */}
      <div
        ref={scrollRef}
        onPointerDown={handleSwipePointerDown}
        onPointerUp={handleSwipePointerUp}
        style={{
          flex: 1, overflowY: 'auto',
          overflowX: colsPerView >= displayDays.length ? 'auto' : 'hidden',
          scrollbarWidth: 'thin',
        }}
      >
        <div style={{
          minWidth: colsPerView >= displayDays.length ? 52 + visibleDays.length * MIN_COL_W : undefined,
        }}>
          {/* Day header + accommodation row, pinned together to the top of
              the scroll box — the accommodation row must ride along with
              the header (not scroll away with the early-morning hours) or
              "tonight's stay" would vanish the instant the view auto-scrolls
              to 8am on mount. */}
          <div style={{ position: 'sticky', top: 0, zIndex: 4, background: PALETTE.card }}>
          <div style={{ display: 'flex', borderBottom: `0.5px solid ${PALETTE.ruleSoft}` }}>
            <div style={{ width: 52, flexShrink: 0, position: 'sticky', left: 0, zIndex: 1, background: PALETTE.card }} />
            {visibleDays.map((day, vi) => {
              const dayIdx = safeOffset + vi;
              const hasNote = !!dayNotes[dayIdx];
              return (
                <div key={vi} style={{ flex: 1, minWidth: MIN_COL_W, position: 'relative', padding: '7px 10px', borderLeft: `0.5px solid ${PALETTE.ruleSoft}`, fontFamily: 'JetBrains Mono', fontSize: 10, letterSpacing: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ color: PALETTE.ink, fontWeight: 700 }}>{day.label}</span>
                  <span style={{ color: PALETTE.inkMute }}>{day.date}</span>
                  {onSaveDayNote && (
                    <button
                      title={hasNote ? dayNotes[dayIdx] : 'Add a note for this day'}
                      onClick={() => { setNoteDay(noteDay === dayIdx ? null : dayIdx); setNoteDraft(dayNotes[dayIdx] || ''); }}
                      style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', padding: 2, marginLeft: 'auto', display: 'flex', color: hasNote ? accent : PALETTE.rule, flexShrink: 0 }}
                    >
                      <Icon name="pencil" size={10} />
                    </button>
                  )}
                  </div>
                  {day.destCity && (
                    <span style={{ color: accent, fontSize: 9, fontWeight: 600, textTransform: 'uppercase' }}>{day.destCity}</span>
                  )}
                  {noteDay === dayIdx && (
                    <div data-day-note="1" onClick={(e) => e.stopPropagation()} style={{
                      position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 20, width: 220,
                      background: PALETTE.paper, border: `0.5px solid ${PALETTE.rule}`, borderRadius: 10,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.16)', padding: 10,
                      display: 'flex', flexDirection: 'column', gap: 6,
                    }}>
                      <textarea
                        autoFocus value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
                        placeholder="Note for this day…" rows={3}
                        style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: 8, background: PALETTE.card, fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, outline: 'none', resize: 'none' }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button onClick={() => setNoteDay(null)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 11 }}>Cancel</button>
                        <button onClick={() => { onSaveDayNote(dayIdx, noteDraft.trim()); setNoteDay(null); }} style={{ appearance: 'none', border: 0, background: accent, color: '#fff', cursor: 'pointer', borderRadius: 6, padding: '4px 10px', fontFamily: 'Inter', fontSize: 11, fontWeight: 600 }}>Save</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Accommodation row — placed above the grid (not below it) so
              tonight's stay is the first thing you see for each day. */}
          <div style={{ display: 'flex', flexShrink: 0, borderBottom: `0.5px solid ${PALETTE.ruleSoft}`, background: PALETTE.card }}>
              <div style={{ width: 52, flexShrink: 0, position: 'sticky', left: 0, zIndex: 3, background: PALETTE.card, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8 }}>
                <Icon name="bed" size={13} style={{ color: PALETTE.inkMute }} />
              </div>
              {visibleDays.map((_, vi) => {
                const dayIdx = safeOffset + vi;
                const accom = accommodations[dayIdx];
                const isBooked = accomBookedByDay[dayIdx];
                const isEditing = editingAccomDay === dayIdx;
                if (isEditing) {
                  return (
                    <div key={vi} style={{ flex: 1, minWidth: MIN_COL_W, padding: '4px 8px', borderLeft: `0.5px solid ${PALETTE.ruleSoft}`, display: 'flex', alignItems: 'center', minHeight: 30 }}>
                      <input
                        autoFocus value={accomDraft} onChange={e => setAccomDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { onSaveAccommodation(dayIdx, { name: accomDraft.trim() }); setEditingAccomDay(null); } if (e.key === 'Escape') setEditingAccomDay(null); }}
                        onBlur={() => { onSaveAccommodation(dayIdx, { name: accomDraft.trim() }); setEditingAccomDay(null); }}
                        placeholder="Hotel name…"
                        style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1px solid ${accent}`, borderRadius: 6, padding: '4px 6px', background: PALETTE.card, fontFamily: 'Inter', fontSize: 11, color: PALETTE.ink, outline: 'none' }}
                      />
                    </div>
                  );
                }
                return (
                  <div
                    key={vi}
                    onClick={onSaveAccommodation ? () => { setEditingAccomDay(dayIdx); setAccomDraft(accom?.name || ''); } : undefined}
                    style={{ flex: 1, minWidth: MIN_COL_W, padding: '5px 8px', borderLeft: `0.5px solid ${PALETTE.ruleSoft}`, display: 'flex', alignItems: 'center', gap: 5, minHeight: 30, cursor: onSaveAccommodation ? 'pointer' : 'default' }}
                  >
                    {accom?.name ? (
                      <>
                        <span style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 500, color: PALETTE.ink, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{accom.name}</span>
                        <span title={isBooked ? 'Booked' : 'Not booked yet'} style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: isBooked ? '#16A34A' : PALETTE.inkMute, letterSpacing: 0.3, flexShrink: 0 }}>
                          {isBooked ? '✓' : '·'}
                        </span>
                      </>
                    ) : onSaveAccommodation ? (
                      <span style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.rule }}>+ Add hotel</span>
                    ) : null}
                  </div>
                );
              })}
          </div>
          </div>

          {/* Grid body */}
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            {/* Time gutter — sticky to the left of the scroll box */}
            <div style={{ width: 52, flexShrink: 0, position: 'sticky', left: 0, zIndex: 3, background: PALETTE.paper, borderRight: `0.5px solid ${PALETTE.ruleSoft}`, minHeight: (CAL_END_HOUR - CAL_START_HOUR) * CAL_HOUR_PX }}>
              {CAL_HOURS.map(h => (
                <div key={h} style={{ height: CAL_HOUR_PX * blockHours, display: 'flex', alignItems: 'flex-start', paddingTop: 4, paddingRight: 8, justifyContent: 'flex-end', fontFamily: 'JetBrains Mono', fontSize: 8, color: PALETTE.inkMute, letterSpacing: 0.3, lineHeight: 1, boxSizing: 'border-box' }}>
                  {fmtH(h)}
                </div>
              ))}
            </div>

            {/* Day columns */}
            <div style={{ display: 'flex', flex: 1 }}>
              {visibleDays.map((day, vi) => {
            const actualDayIdx = safeOffset + vi;
            const dayGhost = ghost && ghost.dayIdx === actualDayIdx ? ghost : null;
            const fadedId = ghost?.sourceDayIdx === actualDayIdx && ghost?.dayIdx !== actualDayIdx ? ghost.ideaId : null;
            const laneLayout = computeEventLanes(day.items, effectiveStart, effectiveDur);
            return (
              <div
                key={vi}
                ref={el => { colRefs.current[vi] = el; }}
                style={{ flex: 1, minWidth: MIN_COL_W, position: 'relative', borderLeft: `0.5px solid ${PALETTE.ruleSoft}`, cursor: 'crosshair', boxSizing: 'border-box', touchAction: 'pan-y' }}
                onPointerDown={(e) => handleColPointerDown(e, vi)}
              >
                {/* Grid lines */}
                {CAL_HOURS.map(h => (
                  <React.Fragment key={h}>
                    <div style={{ position: 'absolute', top: (h - CAL_START_HOUR) * CAL_HOUR_PX, left: 0, right: 0, borderTop: `0.5px solid ${PALETTE.ruleSoft}`, pointerEvents: 'none' }} />
                    {blockHours >= 1 && (
                      <div style={{ position: 'absolute', top: (h - CAL_START_HOUR + blockHours / 2) * CAL_HOUR_PX, left: '20%', right: 0, borderTop: `0.5px solid ${PALETTE.ruleSoft}55`, pointerEvents: 'none' }} />
                    )}
                  </React.Fragment>
                ))}
                <div style={{ height: (CAL_END_HOUR - CAL_START_HOUR) * CAL_HOUR_PX, pointerEvents: 'none' }} />

                {/* Events */}
                {day.items.map((slot) => {
                  const start = effectiveStart(slot);
                  const dur = effectiveDur(slot);
                  const top = (start - CAL_START_HOUR) * CAL_HOUR_PX;
                  const height = Math.max(dur * CAL_HOUR_PX, HANDLE_H * 2 + 14);
                  const cc = CAL_CAT_COLORS[slot.idea.cat] || CAL_CAT_COLORS.Custom;
                  const { col: laneCol = 0, totalCols: laneCols = 1 } = laneLayout.get(slot.idea.id) || {};
                  const isGhosted = (dayGhost?.ideaId === slot.idea.id) || fadedId === slot.idea.id;
                  const bookingInfo = bookingInfoForIdea(slot.idea, destination);
                  // Only show a booking badge at all when there's a reason to
                  // track one (an explicit reminder or a known booking link)
                  // — otherwise a stray `bookings[id]` entry from before the
                  // reminder was turned off would keep showing "Booked".
                  const trackBooking = !!(bookingInfo || slot.idea.requiresBooking);
                  const isBooked = trackBooking && !!bookings?.[slot.idea.id];
                  const isTentative = !!localOverrides[slot.idea.id]?.tentative;
                  return (
                    <div
                      key={slot.idea.id}
                      style={{
                        position: 'absolute', top, height,
                        left: `calc(3px + (100% - 6px) / ${laneCols} * ${laneCol})`,
                        width: `calc((100% - 6px) / ${laneCols} - ${laneCols > 1 ? 2 : 0}px)`,
                        background: cc.bg, border: `1.5px ${isTentative ? 'dashed' : 'solid'} ${cc.border}`,
                        borderRadius: 6, overflow: 'hidden', boxSizing: 'border-box', zIndex: 1,
                        opacity: isGhosted ? 0.25 : 1,
                      }}
                      onPointerDown={(e) => handleEventPointerDown(e, slot, vi)}
                      onDoubleClick={(e) => { e.stopPropagation(); onEditItem(slot.idea); }}
                      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); if (dragRef.current || pressRef.current) return; setCtxMenu({ idea: slot.idea, x: e.clientX, y: e.clientY, dayIdx: actualDayIdx }); }}
                    >
                      {/* Top resize handle */}
                      <div
                        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: HANDLE_H, cursor: 'n-resize', zIndex: 2 }}
                        onPointerDown={(e) => handleResizePointerDown(e, slot, vi, 'top')}
                      />
                      {/* Booking reminder — a corner badge instead of inline
                          text, so it reads as "ticket (pending) / ticket+tick
                          (booked)" rather than a generic green "Booked" tag.
                          Purely informational (pointer-events off) so it
                          never steals the resize-handle's drag. Sized up from
                          the original 9px — too small to register at a
                          glance on the calendar grid. */}
                      {trackBooking && (
                        <div
                          title={isBooked ? 'Needs booking — booked' : 'Needs booking — not booked yet'}
                          style={{ position: 'absolute', top: 3, right: 4, zIndex: 3, display: 'flex', alignItems: 'center', gap: 2, pointerEvents: 'auto' }}
                        >
                          <Icon name="ticket" size={12} style={{ color: '#D97706' }} />
                          {isBooked && <Icon name="check" size={10} stroke={3} style={{ color: '#16A34A' }} />}
                        </div>
                      )}
                      {/* Content */}
                      <div style={{ padding: `${HANDLE_H + 1}px 6px ${HANDLE_H + 1}px 6px`, cursor: 'grab' }}>
                        <div style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 600, color: cc.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3, display: 'flex', alignItems: 'center', gap: 4 }}>
                          {/* Source-URL link lives in the edit sheet now — a
                              second top-right icon here competed visually
                              with the booking badge. */}
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: trackBooking ? 14 : 0 }}>{slot.idea.title}</span>
                        </div>
                        {height >= 54 && (
                          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 8, color: cc.border, marginTop: 2, opacity: 0.85 }}>
                            {fmtH(start)} – {fmtH(start + dur)}
                          </div>
                        )}
                        {height >= 72 && slot.idea.meta && (
                          <div style={{ fontFamily: 'Inter', fontSize: 10, color: cc.text, opacity: 0.65, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {slot.idea.meta}
                          </div>
                        )}
                      </div>
                      {/* Bottom resize handle */}
                      <div
                        style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: HANDLE_H, cursor: 's-resize', zIndex: 2 }}
                        onPointerDown={(e) => handleResizePointerDown(e, slot, vi, 'bottom')}
                      />
                    </div>
                  );
                })}

                {/* Flight events — colored by chronological order so multiple flights (including a multi-city trip's several legs) stay distinguishable */}
                {(flightByDay[actualDayIdx] || []).map((band, fi) => {
                    const dep = parseFlightHM(band.depTime);
                    const arr = parseFlightHM(band.arrTime);
                    if (dep === null && arr === null) return null;
                    const c = FLIGHT_EVENT_PALETTE[(band.seq ?? 0) % FLIGHT_EVENT_PALETTE.length];
                    const route = [band.origin, band.dest].filter(Boolean).join(' → ');
                    const startH = Math.max(CAL_START_HOUR, dep ?? arr);
                    const endH = Math.min(CAL_END_HOUR, arr ?? (dep + 1));
                    const durH = Math.max(endH - startH, 0.5);
                    const top = (startH - CAL_START_HOUR) * CAL_HOUR_PX;
                    const height = Math.max(durH * CAL_HOUR_PX, 30);
                    return (
                      <div
                        key={fi}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); onOpenLeg?.('flight', band); }}
                        title="View flight details"
                        style={{
                          position: 'absolute', top, left: 3, right: 3, height,
                          background: c.bg, border: `1.5px solid ${c.border}`,
                          borderRadius: 6, zIndex: 2, cursor: 'pointer',
                          boxSizing: 'border-box', padding: '3px 6px', overflow: 'hidden',
                        }}>
                        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: c.mono, fontWeight: 700, letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                          <span>✈️</span>
                          <span>{route || 'Flight'}</span>
                          {band.flightNo && <span style={{ fontWeight: 400, color: c.label }}>{band.flightNo}</span>}
                        </div>
                        {height >= 44 && (dep !== null || arr !== null) && (
                          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 8, color: c.label, marginTop: 1, whiteSpace: 'nowrap' }}>
                            {dep !== null ? fmtH(dep) : '?'} → {arr !== null ? fmtH(Math.min(arr, CAL_END_HOUR)) : '?'}
                            {band.origin && band.dest ? ` · ${band.origin}–${band.dest}` : ''}
                          </div>
                        )}
                      </div>
                    );
                })}

                {/* Booked-activity events — from the Bookings tab, placed at their scheduled time */}
                {(activityByDay[actualDayIdx] || []).map((act, ai) => {
                    const t = parseFlightHM(act.time);
                    if (t === null) return null;
                    const startH = Math.max(CAL_START_HOUR, t);
                    const endH = Math.min(CAL_END_HOUR, t + 1);
                    const durH = Math.max(endH - startH, 0.5);
                    const top = (startH - CAL_START_HOUR) * CAL_HOUR_PX;
                    const height = Math.max(durH * CAL_HOUR_PX, 28);
                    return (
                      <div
                        key={act.id || ai}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); onOpenLeg?.('activity', act); }}
                        title="View activity details"
                        style={{
                          position: 'absolute', top, left: 3, right: 3, height,
                          background: ACTIVITY_EVENT_COLOR.bg, border: `1.5px solid ${ACTIVITY_EVENT_COLOR.border}`,
                          borderRadius: 6, zIndex: 2, cursor: 'pointer',
                          boxSizing: 'border-box', padding: '3px 6px', overflow: 'hidden',
                        }}>
                        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: ACTIVITY_EVENT_COLOR.mono, fontWeight: 700, letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <span>🎟️</span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{act.title || 'Activity'}</span>
                        </div>
                        {height >= 44 && (
                          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 8, color: ACTIVITY_EVENT_COLOR.label, marginTop: 1, whiteSpace: 'nowrap' }}>
                            {fmtH(t)}
                          </div>
                        )}
                      </div>
                    );
                })}

                {/* Ghost */}
                {dayGhost && (
                  <div style={{
                    position: 'absolute',
                    top: (dayGhost.startHour - CAL_START_HOUR) * CAL_HOUR_PX,
                    left: 3, right: 3,
                    height: Math.max((dayGhost.dur || 1) * CAL_HOUR_PX, 26),
                    background: `${accent}28`, border: `2px dashed ${accent}`,
                    borderRadius: 6, boxSizing: 'border-box', zIndex: 2, pointerEvents: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: accent }}>
                      {fmtH(dayGhost.startHour)}{dayGhost.dur >= 0.5 ? ` – ${fmtH(dayGhost.startHour + dayGhost.dur)}` : ''}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom hint bar */}
      <div style={{ flexShrink: 0, padding: '5px 12px', borderTop: `0.5px solid ${PALETTE.ruleSoft}`, display: 'flex', alignItems: 'center' }}>
        <span style={{ fontFamily: 'Inter', fontSize: 10, color: PALETTE.inkMute, lineHeight: 1.3 }}>
          Drag grid to add · drag edges to resize · double-click to edit/remove
        </span>
      </div>

      {/* Context menu */}
      {ctxMenu && (
        <div
          data-ctxmenu="1"
          style={{
            position: 'fixed', top: ctxMenu.y, left: ctxMenu.x, zIndex: 200,
            background: PALETTE.paper, border: `0.5px solid ${PALETTE.rule}`,
            borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
            overflow: 'hidden', minWidth: 170,
          }}
        >
          {[
            { label: 'Edit details', icon: 'pencil', action: () => { onEditItem(ctxMenu.idea); setCtxMenu(null); } },
            ...(onToggleConfirmed ? [
              localOverrides[ctxMenu.idea.id]?.tentative
                ? { label: 'Mark confirmed', icon: 'circleCheck', action: () => { onToggleConfirmed(ctxMenu.idea.id); setCtxMenu(null); } }
                : { label: 'Mark tentative', icon: 'circleDashed', action: () => { onToggleConfirmed(ctxMenu.idea.id); setCtxMenu(null); } },
            ] : []),
            { label: 'Unschedule', icon: 'arrowDn', action: () => { onMoveToParking(ctxMenu.idea.id); setCtxMenu(null); } },
            { label: 'Remove from trip', icon: 'x', action: () => { onRemoveFromTrip(ctxMenu.idea.id); setCtxMenu(null); }, danger: true },
          ].map(({ label, icon, action, danger }) => (
            <button key={label} onClick={action}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', appearance: 'none', border: 0, borderTop: `0.5px solid ${PALETTE.ruleSoft}`, background: 'transparent', fontFamily: 'Inter', fontSize: 13, color: danger ? PALETTE.red : PALETTE.ink, cursor: 'pointer', textAlign: 'left' }}
            >
              <Icon name={icon} size={13} style={{ color: danger ? PALETTE.red : PALETTE.inkMute, flexShrink: 0 }} />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

